import { useEffect } from 'react';
import css from './index.module.scss';
import { Button, IconNode, Modal, Typography } from '@components/base';
import { Controller, useForm } from 'react-hook-form';
import { getTdsTcs, updateTdsTcs } from '@services/finance.service';
import notify from '@helpers/toastify-helper';
import Images from '@assets/images';
import { TextField } from '@components/common';
import { yupResolver } from '@hookform/resolvers/yup';
import { AddTDSTCSSchema } from '@helpers/yup/finance.schema';

interface IForm {
  tds: number;
  tcs: number;
}

interface AddTDSTCSProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
}

const AddTDSTCS = (props: AddTDSTCSProps) => {
  const { open, onClose, invoiceId } = props;

  const form = useForm<IForm>({
    resolver: yupResolver(AddTDSTCSSchema),
    defaultValues: {
      tds: 0,
      tcs: 0
    }
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = form;

  const fetchTdsTcs = async (id: string) => {
    const response = await getTdsTcs(id);
    if (response.success) {
      form.reset({
        tds: response.data.tds_amount,
        tcs: response.data.tcs_amount
      });
    } else {
      notify({
        severity: 'error',
        message: response?.message ?? 'Something went wrong!'
      });
    }
  };

  useEffect(() => {
    fetchTdsTcs(invoiceId);
  }, []);

  const onFormSubmit = async (data: IForm) => {
    const reqBody = {
      tds_amount: Number(data.tds ?? 0),
      tcs_amount: Number(data.tcs ?? 0)
    };
    const response = await updateTdsTcs(invoiceId, reqBody);
    if (response.success) {
      notify({
        severity: 'success',
        message: response?.message ?? 'TDS/TCS updated successfully!'
      });
      onClose();
    } else {
      notify({
        severity: 'error',
        message: response?.message ?? 'Something went wrong!'
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <form noValidate onSubmit={handleSubmit(onFormSubmit)}>
        <div className={css.mainWrapper}>
          <section className={css.modalHeader}>
            <Typography variant="h4">Add TDS/TCS</Typography>
            <IconNode
              src={Images.crossBlack}
              alt="close icon"
              component="button"
              className={css.closeButton}
              onClick={onClose}
            />
          </section>
          <section className={css.content}>
            <div className={css.rowWrapper}>
              <Controller
                control={control}
                name="tds"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="TDS Amount"
                    error={fieldState.invalid}
                    endIcon={
                      fieldState.invalid && <IconNode src={Images.alertError} alt="Error Icon" />
                    }
                    helperText={fieldState.error?.message}
                    rootClassName={css.inputWrapper}
                  />
                )}
              />
              <Controller
                control={control}
                name="tcs"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="TCS Amount"
                    error={fieldState.invalid}
                    endIcon={
                      fieldState.invalid && <IconNode src={Images.alertError} alt="Error Icon" />
                    }
                    helperText={fieldState.error?.message}
                    rootClassName={css.inputWrapper}
                  />
                )}
              />
            </div>
          </section>
          <section className={css.modalFooter}>
            <Button variant="outlined-secondary" onClick={onClose}>
              Close
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving' : 'Save'}
            </Button>
          </section>
        </div>
      </form>
    </Modal>
  );
};

export default AddTDSTCS;
